import { Download, FileQuestion } from 'lucide-react';
import { useCallback } from 'react';
import { Button } from '../../../../components/Button';
import { EmptyState } from '../../../../components/EmptyState';
import { ErrorState } from '../../../../components/ErrorState';
import { Modal } from '../../../../components/Modal';
import { Skeleton } from '../../../../components/Skeleton';
import { useBlobPreview } from '../../../../hooks/useBlobPreview';
import { formatBytes } from '../../../../lib/formatBytes';
import { pressReleasesApi } from '../pressReleaseApi';
import type { PressReleaseAttachmentItem } from '../pressReleaseTypes';

/**
 * One attachment, shown at the size the screen allows — an image, a PDF in the browser's own
 * viewer, or a video with its controls.
 *
 * **Everything security-bearing is `useBlobPreview`'s** — the verified type, the inert allow-list
 * and the re-typed blob. This opts in to `video`; nothing else in it decides what may render.
 *
 * **It writes nothing, so it carries no confirm tick** — `<ImagePreviewModal>`'s rule. Its one
 * other control is Download, and it is opened only by a reader holding `download`.
 *
 * **Mounting is what fetches** — the whole file, up to 250 MB, so it mounts only when a tile is opened.
 */
export function AttachmentPreviewModal({
  releaseId,
  attachment,
  onDownload,
  onClose,
}: {
  releaseId: string;
  attachment: PressReleaseAttachmentItem;
  onDownload: () => void;
  onClose: () => void;
}) {
  const load = useCallback(
    (signal: AbortSignal) => pressReleasesApi.download(releaseId, attachment.id, signal),
    [releaseId, attachment.id],
  );

  const { kind, url, loading, error } = useBlobPreview(attachment.id, attachment.contentType, load, { video: true });

  return (
    <Modal
      open
      onClose={onClose}
      title={attachment.fileName}
      size="3xl"
      id="press-release-attachment-preview-modal"
      footer={
        <>
          <Button
            id="press-release-attachment-preview-download"
            variant="secondary"
            leading={<Download className="h-4 w-4" aria-hidden="true" />}
            onClick={onDownload}
          >
            Download
          </Button>
          <Button id="press-release-attachment-preview-close" variant="secondary" onClick={onClose}>
            Close
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-2">
        <p className="font-mono text-xs tabular-nums text-slate-500">
          {attachment.contentType} · {formatBytes(attachment.sizeBytes)}
        </p>

        {kind === 'none' ? (
          <div className="flex min-h-[320px] flex-col items-center justify-center gap-4 rounded-lg bg-slate-50">
            <FileQuestion className="h-10 w-10 text-slate-300" aria-hidden="true" />
            <EmptyState
              minHeight="min-h-0"
              message={`This file is ${attachment.contentType}, which cannot be shown here. Download it to open it.`}
            />
          </div>
        ) : loading ? (
          <Skeleton className="h-[50vh] w-full" />
        ) : error || !url ? (
          <div className="flex min-h-[320px] items-center justify-center rounded-lg bg-slate-50">
            <ErrorState message={error?.message ?? 'Could not load the preview.'} />
          </div>
        ) : kind === 'image' ? (
          <div className="flex items-center justify-center rounded-lg bg-slate-100 p-3">
            {/* The uploaded name is what this image IS as far as the system knows — so it is the alt. */}
            <img src={url} alt={attachment.fileName} className="max-h-[50vh] w-auto max-w-full rounded object-contain" />
          </div>
        ) : kind === 'video' ? (
          <div className="flex items-center justify-center rounded-lg bg-slate-900">
            {/* No captions exist for an uploaded clip; the file name labels it. */}
            <video src={url} controls preload="metadata" aria-label={attachment.fileName} className="max-h-[50vh] w-full">
              <track kind="captions" />
            </video>
          </div>
        ) : (
          <iframe src={url} title={`Preview of ${attachment.fileName}`} className="h-[50vh] w-full rounded-lg border-0 bg-slate-50" />
        )}
      </div>
    </Modal>
  );
}
